import React, { useCallback, useEffect, useState } from 'react';
import {
  Drawer,
  Descriptions,
  Table,
  Tag,
  Typography,
  Spin,
  Alert,
} from 'antd';
import { getInstrument, getInstrumentPrices } from '../services/api';
import { alexforbesBrand } from '../brand/alexforbes';
import { formatDisplayDate } from '../utils/dates';
import { humanizeCode } from '../utils/labels';

const { Text, Title } = Typography;

const formatMoney = (value, currency) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ''}`.trim();
  }
};

const formatDate = (value) => formatDisplayDate(value);

const typeColor = (type) => {
  const c = alexforbesBrand.colors;
  switch (type?.toLowerCase()) {
    case 'stock':
    case 'equity':
      return c.teal;
    case 'etf':
      return c.orange;
    case 'bond':
    case 'fixed_income':
      return c.orangeLight;
    default:
      return c.neutral;
  }
};

const InstrumentDetailDrawer = ({ instrumentId, open, onClose, initialInstrument }) => {
  const [instrument, setInstrument] = useState(initialInstrument || null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesError, setPricesError] = useState(null);
  const [pricePagination, setPricePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    if (!open || !instrumentId) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setInstrument(initialInstrument || null);

    (async () => {
      try {
        const data = await getInstrument(instrumentId);
        if (!cancelled) setInstrument(data);
      } catch {
        if (!cancelled) {
          setDetailError('Could not load instrument details.');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, instrumentId, initialInstrument]);

  const loadPrices = useCallback(
    async (page, pageSize) => {
      if (!instrumentId) return;
      setPricesLoading(true);
      setPricesError(null);
      try {
        const data = await getInstrumentPrices(instrumentId, page, pageSize);
        setPrices(data.results || []);
        setPricePagination({
          current: page,
          pageSize,
          total: data.count ?? 0,
        });
      } catch {
        setPricesError('Could not load price history.');
        setPrices([]);
      } finally {
        setPricesLoading(false);
      }
    },
    [instrumentId]
  );

  useEffect(() => {
    if (!open || !instrumentId) return;
    loadPrices(1, pricePagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset to page 1 when drawer opens
  }, [open, instrumentId, loadPrices]);

  const handlePriceTableChange = (pag) => {
    loadPrices(pag.current, pag.pageSize);
  };

  const currency = instrument?.currency || 'EUR';

  const priceColumns = [
    {
      title: 'As of date',
      dataIndex: 'as_of_date',
      key: 'as_of_date',
      render: (v) => formatDate(v),
      sorter: false,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (v) => (
        <Text strong style={{ color: alexforbesBrand.colors.teal }}>
          {formatMoney(v, currency)}
        </Text>
      ),
    },
    {
      title: 'Recorded',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => formatDate(v),
    },
  ];

  return (
    <Drawer
      title={instrument ? `${instrument.symbol} — ${instrument.name}` : 'Instrument details'}
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {detailLoading && !instrument ? (
        <Spin />
      ) : (
        <>
          {detailError && (
            <Alert type="warning" showIcon message={detailError} style={{ marginBottom: 16 }} />
          )}
          {instrument && (
            <>
              <Descriptions bordered size="small" column={1} style={{ marginBottom: 24 }}>
                <Descriptions.Item label="Symbol">{instrument.symbol}</Descriptions.Item>
                <Descriptions.Item label="ISIN">{instrument.isin || '—'}</Descriptions.Item>
                <Descriptions.Item label="Name">{instrument.name}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color={typeColor(instrument.type)}>
                    {humanizeCode(instrument.type || 'unknown')}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Exchange">
                  {instrument.exchange?.code || instrument.exchange?.name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Fund currency (native NAV)">
                  {currency}
                </Descriptions.Item>
                <Descriptions.Item label="Pricing proxy">
                  {instrument.ticker || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Valuation note">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Prices are stored in fund currency. Portfolios in another reporting currency
                    convert via FX at valuation time only.
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Latest price">
                  {formatMoney(instrument.price, currency)}
                </Descriptions.Item>
                <Descriptions.Item label="Last price date">
                  {formatDate(instrument.last_price_date)}
                </Descriptions.Item>
                {instrument.asset_class ? (
                  <Descriptions.Item label="Asset class">{instrument.asset_class}</Descriptions.Item>
                ) : null}
                {instrument.issuer ? (
                  <Descriptions.Item label="Issuer">{instrument.issuer}</Descriptions.Item>
                ) : null}
                {instrument.sector ? (
                  <Descriptions.Item label="Sector">{instrument.sector}</Descriptions.Item>
                ) : null}
                {instrument.region ? (
                  <Descriptions.Item label="Region">{instrument.region}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Tradable">
                  {instrument.tradable ? 'Yes' : 'No'}
                </Descriptions.Item>
                {instrument.minimum_trade_amount != null &&
                Number(instrument.minimum_trade_amount) > 0 ? (
                  <Descriptions.Item label="Min. trade amount">
                    {formatMoney(instrument.minimum_trade_amount, currency)}
                  </Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Dealing frequency">
                  {humanizeCode(instrument.dealing_frequency) || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Notice period">
                  {instrument.notice_period_days
                    ? `${instrument.notice_period_days} days`
                    : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Lock-up">
                  {instrument.lockup_months
                    ? `${instrument.lockup_months} months`
                    : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Redemption gate">
                  {instrument.gate_percent != null && instrument.gate_percent !== ''
                    ? `${instrument.gate_percent}% per dealing date`
                    : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Redemption settlement">
                  {instrument.redemption_settlement_days
                    ? `${instrument.redemption_settlement_days} days`
                    : '—'}
                </Descriptions.Item>
              </Descriptions>

              <Title level={5} style={{ marginBottom: 12 }}>
                Price history
              </Title>
              {pricesError && (
                <Alert type="error" showIcon message={pricesError} style={{ marginBottom: 12 }} />
              )}
              <Table
                rowKey="id"
                size="small"
                columns={priceColumns}
                dataSource={prices}
                loading={pricesLoading}
                pagination={{
                  current: pricePagination.current,
                  pageSize: pricePagination.pageSize,
                  total: pricePagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total) => `${total} price records`,
                }}
                onChange={handlePriceTableChange}
                locale={{ emptyText: 'No historical prices recorded yet' }}
              />
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default InstrumentDetailDrawer;
